import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import founderPortrait from '@/assets/founder-portrait.jpg';
import gracePortrait from '@/assets/partner-grace-gkii.jpg';
import teamHero from '@/assets/team-hero.jpg';
import SEO from '../components/SEO';

const partners = [
  {
    name: 'Omollo Victor',
    title: 'Founding Partner',
    qualifications: 'LLB Hons | CPM | Trainee Advocate | Student Member CIArb | Certified Secretary (Advanced)',
    image: founderPortrait,
    to: '/founder-profile',
  },
  {
    name: 'Grace Gakii',
    title: 'Partner',
    qualifications: 'LLB Hons | CPM | Trainee Advocate',
    image: gracePortrait,
    to: '/partners/grace-gakii',
  },
];

const TeamProfile = () => {
  const [revealedIndex, setRevealedIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handlePointerDown = (e: PointerEvent) => {
      if (e.pointerType !== 'touch' && e.pointerType !== 'pen') return;
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setRevealedIndex(null);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Team Profile — Law and Code Africa"
        description="Meet the partners behind Law and Code Africa — Omollo Victor (Founding Partner) and Grace Gakii (Partner)."
        path="/team-profile"
      />

      <section className="relative py-24 md:py-32 overflow-hidden">
        <img
          src={teamHero}
          alt="Diverse legal professionals at Law and Code Africa"
          className="absolute inset-0 w-full h-full object-cover object-[center_30%]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-primary/40 via-primary/30 to-primary/80" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 text-center text-white">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Our People</h1>
          <p className="text-lg md:text-xl text-blue-100 max-w-3xl mx-auto">
            The partners advancing legal commentary, ADR, and legal-tech at{' '}
            <span className="whitespace-nowrap">Law&nbsp;and&nbsp;Code&nbsp;Africa</span>.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 md:px-8 py-16">
        <div ref={containerRef} className="grid grid-cols-1 sm:grid-cols-2 gap-8 md:gap-12 max-w-3xl mx-auto">
          {partners.map((p, idx) => {
            const isRevealed = revealedIndex === idx;
            return (
              <Link
                to={p.to}
                key={p.name}
                onTouchStart={() => setRevealedIndex(idx)}
                onPointerDown={(e) => {
                  if (e.pointerType === 'touch' || e.pointerType === 'pen') {
                    setRevealedIndex(idx);
                  }
                }}
                className={`group block relative overflow-hidden rounded-lg shadow-md hover:shadow-2xl transition-shadow duration-300 ${isRevealed ? 'shadow-2xl' : ''}`}
              >
                <div className="aspect-[4/5] overflow-hidden bg-gray-100">
                  <img
                    src={p.image}
                    alt={`${p.name} - ${p.title}`}
                    loading="lazy"
                    className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${isRevealed ? 'scale-105' : ''}`}
                  />
                </div>
                <div className={`absolute inset-0 bg-gradient-to-t from-primary/95 via-primary/60 to-transparent transition-opacity duration-300 flex flex-col justify-end p-6 text-white ${isRevealed ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                  <h2 className="text-2xl md:text-3xl font-bold mb-1">{p.name}</h2>
                  <p className="text-base md:text-lg text-blue-100 font-medium mb-2">{p.title}</p>
                  <p className="text-sm text-blue-50/90">{p.qualifications}</p>
                  <span className="mt-4 inline-block text-xs uppercase tracking-widest border-b border-white/60 pb-1 w-fit">
                    View Profile →
                  </span>
                </div>
                <div className="sm:hidden p-4 text-center bg-white">
                  <h2 className="text-xl font-bold text-primary">{p.name}</h2>
                  <p className="text-sm text-secondary font-medium">{p.title}</p>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default TeamProfile;
